<template>
  <section v-if="projection" class="financial-overview" aria-label="Financial overview">
    <header class="finance-heading"><h2>Financial overview</h2><span>USD / month · estimate</span></header>
    <p class="finance-note">Reference-price value on {{ on }}. These are estimates, not payment totals or contracted MRR.</p>
    <div class="finance-summary">
      <div class="finance-card">
        <span>{{ projection.unpriced ? 'Active monthly value · partial' : 'Active monthly value' }}</span>
        <strong>{{ money(projection.activeMonthly) }}</strong>
        <small>{{ projection.bundleClinics }} bundle estimates + {{ projection.directionPlacements }} direction placements</small>
      </div>
      <div class="finance-card opportunity">
        <span>Additional from direction slots</span><strong>{{ money(projection.directionPotential) }}</strong>
        <small>Up to {{ projection.freeSlots }} slots at guide prices</small>
      </div>
      <div class="finance-card">
        <span>Full-grid packages available</span><strong>{{ projection.bundleFree }}</strong>
        <small>{{ projection.homepageFree }} homepage places free · all directions must also have room</small>
      </div>
    </div>
    <p v-if="projection.unpriced" class="finance-warning" role="status">{{ projection.unpriced }} clinic/direction combinations are outside the current catalog and unpriced. The active value is a partial subtotal.</p>

    <details class="finance-details">
      <summary>Monthly price guide and calculation by clinic</summary>
      <p class="finance-note">Main-grid reference: {{ money(prices.bundle) }}. Directions: A {{ money(prices.A) }}, B {{ money(prices.B) }}, C {{ money(prices.C) }} per month. {{ projection.baselineDirections }} other directions use the C baseline.</p>
      <p class="finance-note">For this estimate, homepage or all-direction coverage is valued once per clinic as a bundle. Other coverage is valued once per clinic/direction. Actual packages, discounts and payments may differ.</p>
      <div class="finance-table-wrap">
        <table class="finance-table"><thead><tr><th>Clinic</th><th>Estimate basis</th><th>Monthly value</th></tr></thead>
          <tbody><tr v-for="clinic in projection.clinics" :key="clinic.clinicId">
            <td>{{ clinicName(clinic.clinicId) }}</td>
            <td>{{ clinic.kind === 'bundle-estimate' ? 'One bundle' : clinic.directions.length + ' directions' }}<small v-if="clinic.unknown.length"> · {{ clinic.unknown.length }} unpriced</small></td>
            <td>{{ money(clinic.monthly) }}</td>
          </tr></tbody>
        </table>
      </div>
    </details>

    <div class="opportunities-heading"><h3>Where there is room to sell</h3><span>{{ projection.freeSlots }} direction slots</span></div>
    <p class="finance-note">Potential assumes every free slot is sold at its guide price. Capacity is for the selected day; check the full period before selling.</p>
    <div v-if="opportunities.length" class="finance-table-wrap">
      <table class="finance-table opportunity-table"><thead><tr><th>Direction</th><th>Tier</th><th>Price / month</th><th>Free slots</th><th>Additional / month</th><th></th></tr></thead>
        <tbody><tr v-for="direction in visibleOpportunities" :key="direction.id">
          <td>{{ direction.name }}</td><td>{{ direction.tier }}<small v-if="direction.baseline"> · baseline</small></td>
          <td>{{ money(direction.rate) }}</td><td>{{ direction.free }} / {{ view.slotLimit }}</td><td class="potential-value">{{ money(direction.potential) }}</td>
          <td><button type="button" class="finance-link" @click="$emit('view-direction', direction.id)">View slots</button></td>
        </tr></tbody>
      </table>
    </div>
    <p v-else class="finance-note">No free direction slots on this date.</p>
    <button v-if="opportunities.length > 6" type="button" class="finance-link show-opportunities" @click="showAll = !showAll">{{ showAll ? 'Show top 6' : 'Show all ' + opportunities.length + ' directions' }}</button>
    <div class="package-note">
      <strong>Alternative: full-grid packages</strong>
      <p>{{ projection.bundleFree }} packages × {{ money(prices.bundle) }} = {{ money(projection.bundlePotential) }} / month.</p>
      <p>Each uses one homepage slot and one slot in every direction. This overlaps the direction potential above; the amounts cannot be added.</p>
      <p v-if="projection.bottlenecks.length">Limited by: {{ projection.bottlenecks.slice(0, 3).join(', ') }}<template v-if="projection.bottlenecks.length > 3"> and {{ projection.bottlenecks.length - 3 }} more directions</template>.</p>
    </div>
  </section>
</template>

<script>
import { financialProjection, PRICE_GUIDE } from './topPlacementFinance';
import { translatedName } from './topPlacementOverview';

export default {
  props: { view: Object, catalog: Array, on: String },
  data: () => ({ showAll: false, prices: PRICE_GUIDE }),
  computed: {
    projection() { return financialProjection(this.view, this.catalog, this.on); },
    opportunities() { return (this.projection?.inventory || []).filter(item => item.free > 0); },
    visibleOpportunities() { return this.showAll ? this.opportunities : this.opportunities.slice(0, 6); },
  },
  methods: {
    money(value) { return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value); },
    clinicName(id) { return translatedName(this.view.clinics[id]?.name) || 'Clinic #' + id; },
  },
};
</script>

<style scoped>
.financial-overview { background: #fff; border: 1px solid #e0e6ec; border-radius: 8px; padding: 20px; margin: 20px 0; }
.finance-heading, .opportunities-heading { display: flex; justify-content: space-between; align-items: baseline; flex-wrap: wrap; gap: 8px; }
.finance-heading h2 { margin: 0; color: #34465a; font-size: 20px; }
.finance-heading > span, .opportunities-heading > span { font-size: 12px; color: #657389; }
.finance-note { color: #657389; font-size: 12px; line-height: 1.6; margin: 10px 0 16px; }
.finance-summary { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px; }
.finance-card { display: flex; flex-direction: column; gap: 8px; padding: 16px; border-radius: 6px; background: #f4f7fb; font-size: 13px; }
.finance-card strong { font-size: 28px; line-height: 1.2; color: #245980; }
.finance-card small { font-size: 12px; color: #657389; }
.finance-card.opportunity { background: #eef8f1; }
.finance-card.opportunity strong, .potential-value { color: #267047; font-weight: 600; }
.finance-warning { color: #8d6024; background: #fff6e8; padding: 12px; margin: 12px 0; font-size: 13px; }
.finance-details { margin-top: 16px; }
.finance-details summary { cursor: pointer; color: #266b9e; font-size: 13px; }
.opportunities-heading { margin-top: 26px; }
.opportunities-heading h3 { color: #34465a; font-size: 16px; margin: 0; }
.finance-table-wrap { overflow-x: auto; }
.finance-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.finance-table th { color: #657389; font-size: 11px; font-weight: 500; text-align: left; white-space: nowrap; }
.finance-table th, .finance-table td { padding: 12px 10px; border-bottom: 1px solid #e6ebf0; }
.finance-table td:first-child { min-width: 150px; }
.finance-table td:not(:first-child) { white-space: nowrap; }
.finance-table small { font-size: 11px; color: #657389; }
.finance-link { border: 0; background: transparent; color: #266b9e; cursor: pointer; font-size: 12px; padding: 6px 0; white-space: nowrap; }
.finance-link:focus-visible { outline: 3px solid #8dbce0; outline-offset: 2px; }
.show-opportunities { margin-top: 8px; }
.package-note { font-size: 12px; color: #657389; background: #f7f9fb; padding: 14px; border-radius: 5px; margin-top: 16px; line-height: 1.5; }
.package-note strong { color: #34465a; }
.package-note p { margin: 5px 0 0; }
@media (max-width: 800px) { .finance-summary { grid-template-columns: 1fr; } .financial-overview { padding: 14px; } }
</style>
